import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Brand } from "../components/brand";
import { toggleAnswerSelection } from "../answer-selection";
import { FeedbackButton } from "../components/feedback";
import {
  BonusRewardStatus,
  JokerStatus,
  LivesStatus,
  RankSpotlight,
  ScoreStatus,
  rankValue,
} from "../components/game-status";
import { RoomImage, SharedAudio } from "../components/media";
import { ParticipantAnswerOptions } from "../components/participant-answer-options";
import { QuizBoard } from "../components/quiz-board";
import { RankLadder } from "../components/rank-ladder";
import { ConnectionStatus, ErrorBanner } from "../components/status";
import { Timer } from "../components/timer";
import { useRoomConnection } from "../hooks/use-room-connection";
import { loadSession } from "../session";
import { participantSubmissionMarks, useFiftyFiftyComplete } from "../question-presentation";

export function ParticipantPage() {
  const { roomId } = useParams();
  const session = roomId ? loadSession(roomId, "participant") : undefined;
  const { projection, status, error, setError, sendCommand } = useRoomConnection(roomId, session?.token);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [textAnswer, setTextAnswer] = useState("");
  const [submittedQuestionId, setSubmittedQuestionId] = useState<string>();
  const fiftyFiftyComplete = useFiftyFiftyComplete(projection?.fiftyFiftyAnimation);
  const ownPlayer = useMemo(
    () => projection?.players.find((player) => player.playerId === projection.ownPlayerId),
    [projection],
  );
  const ownTeam = projection?.teams.find((team) => team.teamId === ownPlayer?.teamId);
  const buzzerWinner = projection?.players.find((player) => player.playerId === projection.buzzer.winner?.playerId);
  const ownSubmission = projection?.submissions?.find(
    (submission) =>
      submission.playerId === projection.ownPlayerId &&
      submission.questionId === projection.question?.questionId,
  );
  const hasSubmitted =
    Boolean(ownSubmission) || submittedQuestionId === projection?.question?.questionId;
  const ownScore = ownTeam?.score ?? ownPlayer?.score ?? 0;
  const ownRankProgress = ownTeam?.rankProgress ?? ownPlayer?.rankProgress ?? 0;
  const displayedRankValue = projection
    ? rankValue(projection, ownScore, ownRankProgress)
    : 0;
  const displayedRankUnit = projection?.scoringMode === "points" ? "Punkte" : "Fortschritt";
  const displayedAnswerMarks = projection
    ? participantSubmissionMarks(projection.answerMarks, ownSubmission)
    : [];

  useEffect(() => {
    setSelectedOptions([]);
    setTextAnswer("");
    setSubmittedQuestionId(undefined);
  }, [projection?.question?.questionId]);

  useEffect(() => {
    if (ownSubmission || submittedQuestionId !== projection?.question?.questionId) return;
    setSelectedOptions([]);
    setTextAnswer("");
    setSubmittedQuestionId(undefined);
  }, [ownSubmission, projection?.revision, projection?.question?.questionId]);

  if (!session) {
    return (
      <main className="participant-shell">
        <Brand />
        <ErrorBanner message="Teilnehmersitzung fehlt. Bitte erneut über den Raumcode beitreten." />
        <Link className="button button--primary" to="/">
          Zur Startseite
        </Link>
      </main>
    );
  }

  return (
    <main className="participant-shell">
      <Brand />
      <div className="participant-topbar">
        <div>
          <span className="eyebrow">Teilnehmer</span>
          <h1>{ownPlayer?.displayName ?? "Wird verbunden..."}</h1>
        </div>
        <div className="topbar-status">
          <FeedbackButton audience="participant" roomId={roomId} mode={projection?.moduleId ?? "classic"} />
          <ConnectionStatus status={status} />
        </div>
      </div>
      <ErrorBanner message={error} onClose={() => setError(undefined)} />
      {projection ? (
        <>
          <SharedAudio
            roomId={roomId}
            token={session.token}
            mediaId={projection.audio.mediaId}
            playing={projection.audio.status === "playing"}
          />
          <div className="participant-stage-layout">
          <section
            className="card participant-question question-stage"
            style={{ backgroundColor: projection.question?.media.backgroundColor }}
          >
            {projection.question?.media.backgroundMediaId ? (
              <RoomImage
                roomId={roomId}
                token={session.token}
                mediaId={projection.question.media.backgroundMediaId}
                className="question-background"
              />
            ) : null}
            <div className="stage-card__content">
              <div className="stage-card__header">
                <span className="badge">{projection.quizTitle}</span>
                {projection.question?.timerEnabled || projection.timer.status === "running" ? (
                  <Timer timer={projection.timer} />
                ) : null}
              </div>
              {projection.status === "ended" ? (
                <ParticipantEnd projection={projection} />
              ) : ownPlayer?.status === "eliminated" ? (
                <div className="eliminated-screen">
                  <span className="eyebrow">Ausgeschieden</span>
                  <h2>Du bist nicht mehr in der laufenden Runde.</h2>
                  <p>Du kannst das Spiel und den Punktestand weiter verfolgen.</p>
                </div>
              ) : projection.moduleId === "quizboard" && projection.board && !projection.question ? (
                <QuizBoard board={projection.board} compact />
              ) : projection.question ? (
                <>
                  <span className="eyebrow">
                    {projection.moduleId === "quizboard"
                      ? `${projection.question.points} Punkte`
                      : `Frage ${projection.currentQuestionIndex + 1}`}
                  </span>
                  {projection.question.questionType === "bonus" ? <span className="badge">Bonusfrage</span> : null}
                  {projection.activePointMultiplier === 2 ? <span className="badge">Doppelte Wertung</span> : null}
                  <BonusRewardStatus projection={projection} />
                  {projection.solutionOptionIds.length ? (
                    <div className="solution-banner">
                      {projection.questionResolution.resolved
                        ? "Frage beendet – die Lösung wird angezeigt."
                        : "Die Lösung wird angezeigt."}
                    </div>
                  ) : null}
                  {projection.jokerAnnouncement ? (
                    <div
                      className={`joker-announcement ${
                        projection.jokerAnnouncement.targetPlayerId === ownPlayer?.playerId
                          ? "joker-announcement--target"
                          : ""
                      }`}
                    >
                      {projection.jokerAnnouncement.message}
                    </div>
                  ) : null}
                  {ownPlayer && projection.ranks.length ? (
                    <RankSpotlight
                      ranks={projection.ranks}
                      progress={displayedRankValue}
                      unitLabel={displayedRankUnit}
                      roomId={roomId}
                      token={session.token}
                    />
                  ) : null}
                  <h2>{projection.question.prompt}</h2>
                  <RoomImage
                    roomId={roomId}
                    token={session.token}
                    mediaId={projection.question.media.imageMediaId}
                    className={`question-media question-media--${projection.question.media.imageSize ?? "normal"}`}
                  />
                  {!projection.participantAnswersEnabled ? (
                    <div className="stream-only-notice">
                      Stream-only-Modus: Der Host steuert die Antworten.
                    </div>
                  ) : null}
                  {projection.question.answerMode === "free-text" ? (
                    projection.participantAnswersEnabled ? (
                        <label>
                          Deine Antwort
                          <textarea
                            value={textAnswer}
                            maxLength={2000}
                            placeholder="Antwort eingeben"
                            onChange={(event) => setTextAnswer(event.target.value)}
                          />
                        </label>
                    ) : null
                  ) : (
                    <ParticipantAnswerOptions
                      answers={projection.question.answers}
                      interactive={projection.participantAnswersEnabled}
                      selectedOptions={selectedOptions}
                      hiddenOptionIds={projection.hiddenOptionIds}
                      answerMarks={displayedAnswerMarks}
                      solutionOptionIds={projection.solutionOptionIds}
                      fiftyFiftyAnimation={projection.fiftyFiftyAnimation}
                      fiftyFiftyComplete={fiftyFiftyComplete}
                      onSelect={(optionId) =>
                        setSelectedOptions((current) =>
                          toggleAnswerSelection(
                            current,
                            optionId,
                            projection.question?.selectionMode ?? "single",
                          ),
                        )
                      }
                    />
                  )}
                  {projection.participantAnswersEnabled ? (
                      <button
                        className="button button--primary button--full"
                        disabled={
                          (projection.question.answerMode === "free-text"
                            ? !textAnswer.trim()
                            : !selectedOptions.length) ||
                          hasSubmitted
                        }
                        onClick={() => {
                          sendCommand(
                            projection.question?.answerMode === "free-text"
                              ? { type: "SUBMIT_ANSWER", text: textAnswer }
                              : { type: "SUBMIT_ANSWER", optionIds: selectedOptions },
                          );
                          setSubmittedQuestionId(projection.question?.questionId);
                        }}
                      >
                        {hasSubmitted ? "Antwort gesendet" : "Antwort senden"}
                      </button>
                  ) : null}
                  {projection.allowParticipantJokers && ownPlayer && projection.jokers.length ? (
                    <div className="participant-joker-actions">
                      <strong>Deine Joker</strong>
                      {projection.jokers.map((joker) => {
                        const count = ownPlayer.jokerCounts[joker.jokerId] ?? 0;
                        const pending = projection.jokerRequests?.some(
                          (request) => request.jokerId === joker.jokerId && request.status === "pending",
                        );
                        return (
                          <button
                            className="button button--ghost"
                            disabled={count < 1 || pending}
                            key={joker.jokerId}
                            onClick={() => sendCommand({ type: "REQUEST_JOKER", jokerId: joker.jokerId })}
                          >
                            {joker.type === "fifty-fifty" ? `${joker.name} einsetzen` : `${joker.name} anfragen`}
                            {pending ? " · wartet" : ` · ${count}x`}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="empty-state">Warte auf den Start durch den Host.</div>
              )}
            </div>
          </section>
            {projection.ranks.length && ownPlayer ? (
              <RankLadder
                ranks={projection.ranks}
                entries={[
                  ownTeam
                    ? {
                        id: ownTeam.teamId,
                        label: ownTeam.name,
                        progress: rankValue(projection, ownTeam.score, ownTeam.rankProgress),
                        color: ownTeam.color,
                      }
                    : {
                        id: ownPlayer.playerId,
                        label: ownPlayer.displayName,
                        progress: rankValue(projection, ownPlayer.score, ownPlayer.rankProgress),
                      },
                ]}
                primaryEntryId={ownTeam?.teamId ?? ownPlayer.playerId}
                unitLabel={displayedRankUnit}
                roomId={roomId}
                token={session.token}
              />
            ) : null}
          </div>

          {projection.status === "lobby" && projection.allowSelfTeamSelection && projection.teams.length ? (
            <section className="card participant-team-choice">
              <h2>Team wählen</h2>
              <div className="button-row button-row--wrap">
                {projection.teams.map((team) => (
                  <button
                    className="button button--ghost"
                    style={{ borderColor: team.color }}
                    key={team.teamId}
                    onClick={() => sendCommand({ type: "ASSIGN_PLAYER", playerId: ownPlayer?.playerId ?? "", teamId: team.teamId })}
                  >
                    <RoomImage
                      roomId={roomId}
                      token={session.token}
                      mediaId={team.logoMediaId}
                      className="team-logo"
                    />
                    {team.name}
                  </button>
                ))}
              </div>
            </section>
          ) : null}

          <button
            className={`buzzer-button buzzer-button--${projection.buzzer.status}`}
            disabled={projection.buzzer.status !== "open" || ownPlayer?.status !== "active"}
            onClick={() => sendCommand({ type: "BUZZ" })}
          >
            {projection.buzzer.winner?.playerId === ownPlayer?.playerId
              ? "Bereits gebuzzert – du warst zuerst"
              : projection.buzzer.status === "open"
                ? "Buzzer aktiv – BUZZ!"
                : buzzerWinner
                  ? `${buzzerWinner.displayName} war zuerst`
                  : "Buzzer gesperrt"}
          </button>
          <section className="participant-score">
            {ownTeam ? (
              <span className="participant-team" style={{ color: ownTeam.color }}>
                <RoomImage
                  roomId={roomId}
                  token={session.token}
                  mediaId={ownTeam.logoMediaId}
                  className="team-logo"
                />
                {ownTeam.name}
              </span>
            ) : null}
            {ownPlayer ? (
              <ScoreStatus
                projection={projection}
                score={ownTeam?.score ?? ownPlayer.score}
                rankProgress={ownTeam?.rankProgress ?? ownPlayer.rankProgress}
                roomId={roomId}
                token={session.token}
              />
            ) : null}
            <LivesStatus projection={projection} lives={ownPlayer?.lives ?? 0} />
            <JokerStatus
              jokers={projection.jokers}
              counts={
                projection.allowParticipantJokers
                  ? ownPlayer?.jokerCounts ?? {}
                  : ownTeam?.jokerCounts ?? ownPlayer?.jokerCounts ?? {}
              }
              roomId={roomId}
              token={session.token}
            />
          </section>
        </>
      ) : (
        <div className="card empty-state">Raumzustand wird geladen...</div>
      )}
    </main>
  );
}

function ParticipantEnd({ projection }: { projection: NonNullable<ReturnType<typeof useRoomConnection>["projection"]> }) {
  const entries = [...(projection.teams.length ? projection.teams : projection.players)].sort((left, right) =>
    projection.scoringMode === "rank-progress"
      ? right.rankProgress - left.rankProgress
      : right.score - left.score,
  );
  return (
    <div className="end-screen">
      <span className="eyebrow">Endstand</span>
      <h2>Danke fürs Mitspielen!</h2>
      {entries.slice(0, 5).map((entry, index) => (
        <div className="end-screen__entry" key={"teamId" in entry ? entry.teamId : entry.playerId}>
          <span>{index + 1}</span>
          <strong>{"name" in entry ? entry.name : entry.displayName}</strong>
          <b>
            <ScoreStatus projection={projection} score={entry.score} rankProgress={entry.rankProgress} />
          </b>
        </div>
      ))}
    </div>
  );
}
